<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { AppCheckbox, FontAwesomeIcon } from '@sunlabde/m2m-library';
import { useRouter } from 'vue-router';
import CardCheckoutSummary from '../CardCheckoutSummary.vue';
import errorHandler from '@/utilities/ErrorHandler';
import InputLookup from '@/components/InputLookup.vue';
import { getBillingOccurenceDescription } from '@/models/PlanVariant';
import { getFormattedPrice } from '@/models/Currency';
import { usePlanVariant } from '@/composables/usePlanVariant';
import { getEndDate } from '@/models/Discounts';
import { getDiscountedPriceInCents } from '@/models/Product';
import { useStaticLinkComposer } from '@/utilities/StaticLinks';
import { useCartStore } from '@/store/cart';
import { lookupDiscountCodeForOrder } from '@/api/discount';
import { useMainStore } from '@/store/main';
import { useBillwerkStore } from '@/store/billwerk';
import { useProductsStore } from '@/store/products';
import { useLocalization } from '@/models/Localization';
import { getPaymentPeriods } from '@/models/PaymentPeriod';
import { useDoubleOptInMutation } from '@/composables/mutations/useDoubleOptinMutation.js';
import { useAuthStore } from '@/store/auth.js';
import { useGoogleRecaptcha } from '@/plugins/GoogleRecaptcha/useGoogleRecaptcha.js';
import { trackDiscountCodeApplied } from '@/utilities/tracking';

const DISCOUNT_DEFAULT = {
	code: '',
	isValid: false,
	waitingForValidation: false,
	error: '',
};

const staticLinkComposer = useStaticLinkComposer();

const { t, locale } = useI18n();
const router = useRouter();

const { updateDoubleOptinResultAsync } = useDoubleOptInMutation();

const { user } = useAuthStore();
const { getUserTokenForAction } = useGoogleRecaptcha();

const signupForNewsletter = async () => {
	if (!user) {
		return;
	}

	const token = await getUserTokenForAction('signup');

	await updateDoubleOptinResultAsync({
		email: user.email,
		locale: locale.value,
		captchaToken: token,
	});
};

const isSubscribingToNewsletter = ref(false);

const confirmSignup = async () => {
	if (isSubscribingToNewsletter.value) {
		await signupForNewsletter();
	}
	router.push({ name: 'initiatePayment' });
};

const discount = ref({ ...DISCOUNT_DEFAULT });
const useDiscountCode = ref(false);
const cartStore = useCartStore();
const { cart } = storeToRefs(cartStore);

const hasDiscountApplied = computed(() => cartStore.hasDiscountApplied);

const refreshDiscountCode = () => {
	if (hasDiscountApplied.value && cartStore.cart?.CouponCode) {
		useDiscountCode.value = true;
		discount.value.code = cartStore.cart.CouponCode;
		discount.value.isValid = true;
	} else {
		useDiscountCode.value = false;
		discount.value = { ...DISCOUNT_DEFAULT };
	}
};

const appliedDiscount = computed(() => cartStore.appliedDiscount);

const productsStore = useProductsStore();
const { allowsTrialOrder, productsForBillingOccurence } = storeToRefs(productsStore);

const activeProduct = computed(() => productsForBillingOccurence.value.find((product) => {
	if (!cart.value) return false;

	return cart.value.planVariant.Id === product.planVariant.Id;
}));

onMounted(() => {
	if (activeProduct.value && activeProduct.value.billwerkCouponCode) {
		useDiscountCode.value = true;
		discount.value.code = activeProduct.value.billwerkCouponCode;
	}
});

const { endOfTrialDate } = storeToRefs(useMainStore());

const billwerkStore = useBillwerkStore();
const { internalOrder, selectedPaymentMethod } = storeToRefs(billwerkStore);

const onDiscountCodeValidationSubmit = async () => {
	if (!internalOrder.value) {
		return;
	}

	const oldDiscount = appliedDiscount.value;

	discount.value.waitingForValidation = true;
	const resolvedDiscount = await lookupDiscountCodeForOrder(discount.value.code, internalOrder.value);

	if (resolvedDiscount.isErr()) {
		errorHandler.report(resolvedDiscount.error);
		discount.value.isValid = false;
		discount.value.error = t('input.error.invalidCouponCode');
		discount.value.waitingForValidation = false;
		return;
	}

	if (oldDiscount && resolvedDiscount.value.Effect.ReductionPercent < oldDiscount.Effect.ReductionPercent) {
		errorHandler.report({
			name: 'Discount Error',
			message: 'Larger discount can not be overwritten by a smaller discount',
		});
		discount.value.isValid = false;
		discount.value.waitingForValidation = false;
		discount.value.error = t('input.error.overwriteLargerDiscount');
		return;
	}

	const _endOfTrialDate = allowsTrialOrder.value ? endOfTrialDate.value : undefined;

	cartStore.addDiscount({ ...resolvedDiscount.value, code: discount.value.code }, _endOfTrialDate);
	discount.value.isValid = true;
	discount.value.waitingForValidation = false;
	discount.value.error = '';

	trackDiscountCodeApplied(discount.value.code, activeProduct.value?.billwerkCouponCode);
};

const onDiscountCodeCheckboxChange = () => {
	if (useDiscountCode.value) {
		cartStore.removeAllDiscounts();
		refreshDiscountCode();
		return;
	}

	useDiscountCode.value = true;
};

const selectedPlanVariant = computed(() => cartStore.cart?.planVariant);

const waitingForSubscriptionCreation = ref(false);

const confirmButtonText = computed(() => {
	if (allowsTrialOrder.value) {
		return t('button.confirmOrder_trial');
	}

	return t('button.confirmOrder');
});

const { localizePlural, localize } = useLocalization();

const selectedPlanBillingOccurenceName = computed(() => (selectedPlanVariant.value
	? localizePlural(getBillingOccurenceDescription(selectedPlanVariant.value).name, 1)
	: ''));

const isTrialOrder = computed(() => allowsTrialOrder.value);

const paymentPeriods = computed(() => {
	if (!selectedPlanVariant.value) {
		return [];
	}

	if (isTrialOrder.value) {
		return getPaymentPeriods({
			planVariant: selectedPlanVariant.value,
			trialEndDate: (endOfTrialDate.value).toJSDate(),
			discount: appliedDiscount.value ?? undefined,
		});
	}

	return getPaymentPeriods({
		planVariant: selectedPlanVariant.value,
		discount: appliedDiscount.value ?? undefined,
	});
});

const { recurringFeeInCents, currency, localizedDisplayName } = usePlanVariant({
	discount: appliedDiscount,
	planVariant: selectedPlanVariant,
});

const orderOverviewNote = computed(() => {
	if (!selectedPlanVariant.value) {
		return '';
	}

	const autoRenewalNote = allowsTrialOrder.value
		? t(
			'subscribe.step.orderOverview.info.trial.autoRenewal',
			{ planName: `<strong>${localizedDisplayName.value}</strong>` },
		)
		: t(
			'subscribe.step.orderOverview.info.autoRenewal',
			{ planName: `<strong>${localizedDisplayName.value}</strong>` },
		);

	const cancelAnyTimeNote = t('subscribe.step.orderOverview.info.cancelAnyTime');

	const pricePerBillingOccurence = getFormattedPrice(recurringFeeInCents.value, currency.value ?? 'EUR', { fromCents: true });
	const billingOccurence = selectedPlanBillingOccurenceName.value;

	if (hasDiscountApplied.value && appliedDiscount.value) {
		const discountedPricePerBillingOccurenceInCents = hasDiscountApplied.value
			? getDiscountedPriceInCents({
				planVariant: selectedPlanVariant.value,
				discount: appliedDiscount.value,
			})
			: 0;
		const discountedPricePerBillingOccurence = getFormattedPrice(discountedPricePerBillingOccurenceInCents, currency.value, { fromCents: true });

		let discountNote = t('subscribe.step.orderOverview.info.pricing', {
			billingOccurence,
			pricePerBillingOccurence,
			startDate: `<strong>${DateTime.fromJSDate(getEndDate(appliedDiscount.value)).toLocaleString(DateTime.DATE_SHORT)}</strong>`,
			discountedPricePerBillingOccurence,
		});

		if (allowsTrialOrder.value) {
			discountNote = t('subscribe.step.orderOverview.info.discountedPricing', {
				billingOccurence,
				pricePerBillingOccurence,
				discountStartDate: `<strong>${endOfTrialDate.value
					.setLocale(locale.value)
					.toLocaleString({
						day: '2-digit', month: '2-digit', year: 'numeric',

					})}</strong>`,
				discountEndDate: `<strong>${DateTime.fromJSDate(getEndDate(appliedDiscount.value, endOfTrialDate.value.toJSDate()))
					.setLocale(locale.value)
					.toLocaleString({
						day: '2-digit', month: '2-digit', year: 'numeric',

					})}</strong>`,
				discountedPricePerBillingOccurence,
			});
		}

		return [
			autoRenewalNote,
			discountNote,
			cancelAnyTimeNote,
		].join(' ');
	}

	if (allowsTrialOrder.value) {
		const trialNote = t('subscribe.step.orderOverview.info.pricing', {
			startDate: `<strong>${endOfTrialDate.value
				.setLocale(locale.value)
				.toLocaleString({
					day: '2-digit', month: '2-digit', year: 'numeric',

				})}</strong>`,
			billingOccurence,
			pricePerBillingOccurence,
		});

		return [
			autoRenewalNote,
			trialNote,
			cancelAnyTimeNote,
		].join(' ');
	}

	return [
		autoRenewalNote,
		cancelAnyTimeNote,
	].join(' ');
});
</script>

<template>
	<div>
		<h2
			class="text-center text-[24px] mb-4 mx-auto tablet:w-8/12 desktop:w-10/12 widescreen:w-8/12 font-bold font-serif"
			data-cy="step_title"
		>
			<i18n-t
				keypath="subscribe.step.orderOverview.title"
				scope="global"
			>
				<template #confirm>
					<span class="text-secondary-500">{{ $t('generic.confirm') }}</span>
				</template>
			</i18n-t>
		</h2>
		<p
			class="text-center mb-8 text-gray-700 leading-normal mx-auto phablet:w-8/12 desktop:w-9/12 widescreen:w-8/12"
			data-cy="step_subtitle"
		>
			{{ $t('subscribe.step.orderOverview.subtitle') }}
		</p>
		<div class="w-full mb-8 mx-auto phablet:w-8/12 desktop:w-10/12 widescreen:w-8/12">
			<CardCheckoutSummary
				:payment-periods="paymentPeriods"
				:is-trial-order="isTrialOrder"
			>
				<template #body>
					<div
						v-if="selectedPaymentMethod"
						class="flex justify-between items-center border-t -mx-4 pt-4 mt-4 px-4 mobile:-mx-6 mobile:pt-6 mobile:mt-6 mobile:px-6"
					>
						<div>
							<p class="text-sm mobile:text-[18px] font-bold mb-1">
								{{ $t('subscribe.step.orderOverview.paymentMethod') }}
							</p>
							<p class="text-xs mobile:text-sm">
								{{ localize(selectedPaymentMethod.name) }}
							</p>
						</div>
						<img
							v-if="selectedPaymentMethod.image"
							:src="selectedPaymentMethod.image"
							class="w-20 h-auto bg-white"
							:alt="localize(selectedPaymentMethod.name)"
						>
					</div>
				</template>
				<template #footer>
					<div class="flex flex-col text-center">
						<div class="mb-8 text-gray-800">
							<!-- eslint-disable-next-line vue/no-v-html -->
							<p v-html="orderOverviewNote" />
						</div>
						<AppCheckbox
							v-model="isSubscribingToNewsletter"
							is-centered
							class="mb-4"
						>
							<template #default>
								{{ $t('subscribe.step.orderOverview.checkboxes.newsletter') }}
							</template>
						</AppCheckbox>
						<AppCheckbox
							:model-value="useDiscountCode"
							data-cy="checkbox_useDiscountCode"
							is-centered
							@update:model-value="onDiscountCodeCheckboxChange"
						>
							<template #default>
								{{ $t('subscribe.step.orderOverview.checkboxes.discountCode') }}
							</template>
							<template #checked>
								<div class="w-full">
									<InputLookup
										id="discountCode"
										v-model="discount.code"
										:success="discount.isValid"
										:loading="discount.waitingForValidation"
										:error="discount.error"
										:input-label="$t('input.yourDiscountCode')"
										:button-label="$t('button.redeem')"
										icon="percentage"
										class="justify-center"
										@submit="onDiscountCodeValidationSubmit"
									/>
								</div>
							</template>
						</AppCheckbox>
						<i18n-t
							keypath="view.signup.legalNotes.checkout.text"
							tag="p"
							scope="global"
							class="text-xs my-7 text-gray-700"
						>
							<template #actionTos>
								<a
									:href="staticLinkComposer.compose('termsOfUse')"
									class="text-primary-400 no-underline hover:cursor-pointer hover:underline"
									target="_blank"
									rel="noopener noreferrer"
								>
									{{ $t('view.signup.legalNotes.actionTOS') }}
								</a>
							</template>
							<template #actionCancellationPolicy>
								<a
									:href="staticLinkComposer.compose('dataPrivacy')"
									class="text-primary-400 no-underline hover:cursor-pointer hover:underline"
									target="_blank"
									rel="noopener noreferrer"
								>
									{{ $t('view.signup.legalNotes.actionCancellationPolicy') }}
								</a>
							</template>
						</i18n-t>
						<div class="text-center relative flex justify-center">
							<AppButton
								:is-loading="waitingForSubscriptionCreation"
								:loading-message="$t('button.loadingMessage.waitingForSubscriptionCreation')"
								data-cy="button_confirmSignup"
								icon="lock-alt"
								class="w-full mobile:w-auto"
								@click="confirmSignup"
							>
								<FontAwesomeIcon
									icon="lock-alt"
									class="mr-2"
								/>
								{{ confirmButtonText }}
							</AppButton>
						</div>
					</div>
				</template>
			</CardCheckoutSummary>
		</div>
	</div>
</template>
